import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, Input, signal } from '@angular/core';
import { SettingsService } from 'army-builder-shared/src/lib';
import { map } from 'rxjs';
import { StoreAccountComponent } from './store-account.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { WarlordUserService } from '../settings/user/user.service';
import { IonicModule } from '@ionic/angular';
import { ToastService } from 'army-builder-shared/src/lib/global/toast/toast';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { SubscriptionService } from './subscription.service';
import { VoucherService } from './voucher.service';

@Component({
    selector: 'voucher-details',
    imports: [CommonModule, IonicModule, StoreAccountComponent],
    template: `
        <div>
            <p>
                As an annual subcriber, you are eligible to receive a discount voucher to be used on the Warlord web store.
                @if (!storeAccountLinked()) {
                    Before you can redeem your voucher, you need to link your Warlord web store account with the app.
                }
            </p>

            <!-- <ion-list>
                <ion-item *ngFor="let vc of voucherCodes()" (click)="selectedVoucherDate.set(vc.date)">
                    <ion-label>{{ vc.code }} (generated {{ vc.date | relativeDate }})</ion-label>
                </ion-item>
            </ion-list> -->
            <store-account></store-account>

            @if (storeAccountLinked()) {
                <div>
                    <div class="voucherHeader">
                        <span>
                            <a *ngIf="voucherIndex() < voucherCodes().length - 1" (click)="previousVoucher()">Previous</a>
                        </span>
                        <ion-label>Voucher Code</ion-label>
                        <span>
                            <a *ngIf="voucherIndex() > 0 || (voucherIndex() === 0 && canGenerateNewVoucher())" (click)="nextVoucher()"
                                >Next</a
                            >
                        </span>
                    </div>
                    <div class="voucherCode">
                        @if (!selectedVoucher() && canGenerateNewVoucher()) {
                            <ion-button (click)="generateVoucher()"
                                >Generate Voucher <ion-spinner *ngIf="generating"></ion-spinner
                            ></ion-button>
                        } @else {
                            <span class="value" (click)="copyVoucher()">
                                {{ selectedVoucher()?.code }}
                            </span>
                            <ion-icon name="copy-outline" class="copy" (click)="copyVoucher()"></ion-icon>

                            <div class="storeLink" *ngIf="showStoreLink" (click)="hideStoreLink()">
                                <ion-button (click)="goToStore()">Warlord Web Store</ion-button>
                            </div>
                        }
                    </div>
                </div>
                <div class="voucherFooter">
                    @if (voucherStatus(); as status) {
                        <span [class]="status | lowercase">Voucher Status: {{ status }}</span>
                    }

                    <span *ngIf="showStoreLink">Copied!</span>
                    <span>
                        @if (voucherExpiry(); as expiry) {
                            @if (currentVoucherExpired()) {
                                <span class="expired">Expired on {{ expiry | date }}</span>
                            } @else {
                                <span class="active">Expires on {{ expiry | date }}</span>
                            }
                        }
                    </span>
                </div>
            }
            @if (showDismissReminder) {
                <div class="dismissReminder">
                    <ion-item>
                        <ion-checkbox
                            labelPlacement="end"
                            [checked]="settings()?.global?.showVoucherReminder === false"
                            (ionChange)="toggleDismissReminder($event.detail.checked)"
                            >Don't remind me again about this.</ion-checkbox
                        >
                    </ion-item>
                </div>
            }
        </div>
        <div class="error" *ngIf="error">{{ error }}</div>
    `,
    styles: [
        `
            .voucherCode {
                font-weight: bold;
                font-size: 1.5em;
                text-align: center;
                padding: 20px;
                border: 1px solid #ccc;
                display: flex;
                align-items: center;
                position: relative;
                cursor: pointer;
                justify-content: center;
            }

            .voucherCode .value {
                flex: 1 1 auto;
            }

            .voucherCode .copy {
                flex: 0 0 auto;
                position: absolute;
                right: 20px;
            }

            .voucherCode .storeLink {
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background: rgba(0, 0, 0, 0.5);
                padding: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .voucherFooter,
            .voucherHeader {
                display: flex;
                justify-content: space-between;
                font-weight: bold;
                font-size: 0.8em;
            }

            .voucherFooter > *,
            .voucherHeader > * {
                flex: 0 0 33%;
                text-align: center;
            }

            .voucherFooter > *:first-child,
            .voucherHeader > *:first-child {
                text-align: left;
            }

            .voucherFooter > *:last-child,
            .voucherHeader > *:last-child {
                text-align: right;
            }

            .voucherFooter .invalid,
            .voucherFooter .expired {
                color: red;
            }
            .voucherFooter .active {
                color: green;
            }

            ion-spinner {
                color: black;
            }

            .error {
                margin-left: 0;
                margin-right: 0;
                margin-bottom: 0;
            }

            .dismissReminder {
                display: flex;
                align-items: center;
                margin-top: 10px;
                font-size: 0.9em;
                justify-content: flex-end;
            }

            .dismissReminder ion-item {
                font-size: 1em;
            }
        `
    ],
    standalone: true
})
export class VoucherDetailsComponent {
    settingsService = inject(SettingsService);
    subscriptionService = inject(SubscriptionService);
    voucherService = inject(VoucherService);
    user$ = this.settingsService.login$.pipe(map((l) => l.user));
    stores$ = this.user$.pipe(map((u) => Object.keys(u.stores || {}).filter((key) => !!u.stores[key])));

    @Input()
    showDismissReminder = false;

    user = toSignal(this.user$);

    settings = toSignal(this.settingsService.settings$);
    toast = inject(ToastService);
    webstoreEmail = computed(() => this.settings()?.global?.webstoreEmail);
    preferredStore = computed(() => this.settings()?.global?.preferredStore);
    webstoreCustomerId = computed(() => this.settings().global?.webstoreCustomerId);
    voucherCodes = this.voucherService.voucherCodes;
    voucherExpiry = computed(() => {
        let state = this.voucherDetails()?.voucher;

        if (!state) {
            return null;
        }

        return state.ends_at;
    });

    currentVoucherExpired = computed(() => {
        let expiry = this.voucherExpiry();
        if (!expiry) {
            return false;
        }
        return new Date(expiry) < new Date();
    });

    voucherDetails = signal(null);
    voucherStatus = computed(() => {
        if (this.voucherIndex() === -1) {
            return null;
        }
        let state = this.voucherDetails()?.voucher;
        console.log('Voucher state', state);

        if (!state) {
            return 'Loading...';
        }

        if (!state?.valid_code) {
            return 'Invalid code';
        }

        if (state?.used_code) {
            return 'Used';
        }

        if (!state?.active_code) {
            return 'Expired';
        }

        return 'Active';
    });

    voucherIndex = signal(-1);

    selectedVoucher = computed(() => {
        let index = this.voucherIndex();
        if (index === -1) {
            return null;
        }
        return this.voucherCodes()?.[index];
    });

    showStoreLink = false;

    canGenerateNewVoucher = this.voucherService.canGenerateNewVoucher;

    storeAccountLinked = computed(() => this.webstoreEmail() && this.preferredStore() && this.webstoreCustomerId());

    userService = inject(WarlordUserService);
    error: string;
    generating = false;

    async generateVoucher() {
        this.generating = true;
        let res = await this.userService
            .generateVoucher()
            .then((res) => {
                this.error = null;
                this.generating = false;
                return res;
            })
            .catch((err) => {
                console.error(err);
                this.error = err?.error?.message;
                this.generating = false;
            });

        if (res) {
            this.error = null;
            let vouchers = this.voucherService.voucherCodes() || [];
            vouchers.push({ code: res, date: new Date().toISOString() });
            this.settingsService.updateUserMetadataValue('voucher', vouchers);
        }
    }

    copyVoucher() {
        navigator.clipboard.writeText(this.selectedVoucher()?.code || '');
        this.toast.showToast('', 'Copied voucher to clipboard', { color: 'success', duration: 3000 });
        this.showStoreLink = true;
    }

    hideStoreLink() {
        this.showStoreLink = false;
    }

    constructor(private iab: InAppBrowser) {
        effect(async () => {
            let vc = this.selectedVoucher();
            if (!vc) {
                return null;
            }

            let attempts = 0;
            const updateVoucherDetails = async () => {
                attempts++;
                let voucherDetails = await this.userService.validateWarlordVoucher(vc.code);
                console.log({ voucherDetails });
                if (!voucherDetails?.voucher?.valid_code && attempts < 5) {
                    setTimeout(() => {
                        updateVoucherDetails();
                    }, 2000);
                } else {
                    this.voucherDetails.set(voucherDetails);
                }
            };
            updateVoucherDetails();
        });

        effect(() => {
            if (!this.canGenerateNewVoucher() && this.voucherCodes().length && this.voucherIndex() === -1) {
                this.voucherIndex.set(0);
            }
        });
    }

    goToStore() {
        const store = this.preferredStore()?.toLowerCase() || 'uk';
        const url = `https://store.warlordgames.com/MALORICDIGITAL?store=${store}&utm_source=collabs&utm_medium=web&utm_campaign=maloricdigital`;
        this.iab.create(url);
    }

    nextVoucher() {
        // Direction is inverted because vouchers are sorted newest first
        let index = this.voucherIndex();
        this.voucherIndex.set(index > 0 || (index === 0 && this.canGenerateNewVoucher()) ? index - 1 : index);
        this.voucherDetails.set(null);
        this.showStoreLink = false;
        this.error = null;
    }

    previousVoucher() {
        // Direction is inverted because vouchers are sorted newest first
        let index = this.voucherIndex();
        this.voucherIndex.set(index < this.voucherCodes().length - 1 ? index + 1 : index);
        this.voucherDetails.set(null);
        this.showStoreLink = false;
        this.error = null;
    }

    toggleDismissReminder(checked: boolean) {
        console.log('[Voucher] toggleDismissReminder - checked:', checked, '→ saving showVoucherReminder:', !checked);
        this.settingsService.updateValue('global', 'showVoucherReminder', !checked);
    }
}
